import { i18n } from '@/plugins/i18n';
import store from '@/store';
// Utils
import {
  getRecurrenceBreakdown,
  getLabelsAndDatasetsFromRecurrence,
  recurrenceChartOptions,
} from '@/utils/vhub-utils';
import VitalityCriterion from '@/utils/classes/VitalityCategory/VitalityCriterion';

/** Score based on the number of occurrences of each problem */
export default new VitalityCriterion({
  titleTr: 'VH_PB_TYPE_ZONE',
  key: 'problemPerTypeZone',
  weight: 1,
  defaultChartIndex: 1,

  score: ({ problems }, { start, end }) => {
    const { pbTypesPerId, zonesPerId } = store.getters;
    const filtered = problems.filter(p => start.isBefore(p.created) && end.isAfter(p.created));
    const pbPerZone = [];
    const pbPerType = [];
    const nProblems = filtered.length;
    let j, jlen, itemId;
    // NOTE: A problem may refer to multiple types or zones, so you may see 70% of problems
    // linked to a type while the pareto displays much less occurrences overall
    for (let i = 0; i < nProblems; i++) {
      const pb = filtered[i];
      if (pb.types && pb.types.length) {
        for (j = 0, jlen = pb.types.length; j < jlen; j++) {
          itemId = pb.types[j];
          pbPerType.push({ typeId: itemId, typeText: pbTypesPerId[`${itemId}`].name });
        }
      }
      if (pb.zones && pb.zones.length) {
        for (j = 0, jlen = pb.zones.length; j < jlen; j++) {
          itemId = pb.zones[j];
          pbPerZone.push({ zoneId: itemId, zoneText: zonesPerId[`${itemId}`].name });
        }
      }
    }

    const typesBreakdown = getRecurrenceBreakdown(pbPerType, 'typeId', 'typeText', undefined, 100);
    const zonesBreakdown = getRecurrenceBreakdown(pbPerZone, 'zoneId', 'zoneText', undefined, 100);
    const score = nProblems === 0 ? 0 : (typesBreakdown.score + zonesBreakdown.score) / 2;

    return {
      score,
      data: {
        noTopsFlops: true,
        noCategoryHint: nProblems === 0,
        value: score,
        nProblems,
        typesRecurrence: typesBreakdown.itemsPerRecurrenceId,
        zonesRecurrence: zonesBreakdown.itemsPerRecurrenceId,
        maxType: typesBreakdown.maxOccurrences,
        maxZone: zonesBreakdown.maxOccurrences,
        maxTypeText: typesBreakdown.maxText,
        maxZoneText: zonesBreakdown.maxText,
      },
    };
  },

  text({
    nProblems,
    maxType,
    maxZone,
    maxTypeText,
    maxZoneText,
  }) {
    if (nProblems === 0) {
      return i18n.tc('message.N_PROBLEMS', 0, { nb: 0 });
    } else if (nProblems > 1) {
      const zone = maxZone > 1;
      const type = maxType > 1;
      const maxTypePerc = (maxType * 100 / nProblems).sToFixed(1);
      const maxZonePerc = (maxZone * 100 / nProblems).sToFixed(1);
      const trParams = {
        typePerc: maxTypePerc,
        zonePerc: maxZonePerc,
        typeText: maxTypeText,
        zoneText: maxZoneText,
      };

      if (zone && type) {
        return i18n.t('message.VH_PB_TYPE_ZONE_TEXT_BOTH', trParams);
      } else if (zone) {
        return i18n.t('message.VH_PB_TYPE_ZONE_TEXT_ONLY_Z', trParams);
      } else if (type) {
        return i18n.t('message.VH_PB_TYPE_ZONE_TEXT_ONLY_T', trParams);
      }
    }
    return i18n.t('message.VH_PB_NO_RECURRENCE_TEXT');
  },

  chartData(teamGroups) {
    // Display a stacked bar charts. The zone (and type) where most problems occur will appear first
    return [
      getLabelsAndDatasetsFromRecurrence(teamGroups, this.scoresKey, 'zonesRecurrence'),
      getLabelsAndDatasetsFromRecurrence(teamGroups, this.scoresKey, 'typesRecurrence'),
    ];
  },

  chartOptions() {
    return [recurrenceChartOptions, recurrenceChartOptions];
  },

  chartNames() {
    return [i18n.t('message.LOCATIONS'), i18n.t('message.TYPES')];
  },
});
